import { FormLabelRAC, InputButton, Picker, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { APIError } from "@certn/api";
import { useField } from "formik";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ReviewerLearnMore } from "./ReviewerLearnMore";
import { ReviewerOption } from "./types";

export type AssignReviewerPickerProps = {
  /** List of eligible reviewers (Admins/Managers). */
  reviewers: ReviewerOption[] | undefined;
  /** Whether the reviewers are still loading. */
  isLoading: boolean;
  /** Error from the reviewers fetch, if any. */
  error: unknown;
  /**
   * When true, show a warning if `primary_reviewer` is set but not in the
   * eligible-reviewers list (e.g. reviewer demoted or deactivated).
   */
  enableStaleReviewerWarning?: boolean;
};

export function AssignReviewerPicker({
  reviewers,
  isLoading,
  error,
  enableStaleReviewerWarning = false,
}: AssignReviewerPickerProps) {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [{ value: primaryReviewerId }, meta, helpers] = useField<string | null>(
    "primary_reviewer"
  );
  const showError = meta.touched && meta.error;

  const items = useMemo(
    () =>
      (reviewers ?? []).map((u: ReviewerOption) => {
        const roleNames = u.roles.map((r) => r.name).join(", ");
        const email = u.email ?? "";
        return {
          id: u.id,
          name: u.name?.trim() || email || t("Unknown user"),
          subtitle: [roleNames, email].filter(Boolean).join(" • "),
        };
      }),
    [reviewers, t]
  );

  const selectedReviewer = useMemo(
    () => reviewers?.find((u) => u.id === primaryReviewerId),
    [reviewers, primaryReviewerId]
  );

  const showStaleReviewerWarning =
    enableStaleReviewerWarning &&
    !isLoading &&
    reviewers != null && // API returned data (not error state)
    primaryReviewerId != null && // form has a reviewer ID set
    selectedReviewer == null; // reviewer not found in eligible list (stale)

  const triggerLabel =
    selectedReviewer?.name?.trim() ||
    selectedReviewer?.email ||
    t("Select a reviewer");

  const isForbidden = error instanceof APIError && error.status === 403;
  const selectedKeys = primaryReviewerId ? [primaryReviewerId] : [];

  return (
    <div>
      <Text style="global/heading-medium">{t("Reviewer Details")}</Text>
      <div className={styles.labelRow}>
        <FormLabelRAC isRequired>{t("Primary reviewer")}</FormLabelRAC>
        <ReviewerLearnMore />
      </div>
      <Picker
        hideClear
        showActionBar
        triggerRef={triggerRef as React.RefObject<HTMLDivElement>}
        maxWidth="560px"
        items={items}
        selectedKeys={selectedKeys}
        selectionMode="single"
        testID="reviewer-picker"
        isLoading={isLoading}
        isDisabled={isForbidden}
        actionText={t("Add")}
        label={t("Assign reviewer")}
        onDone={(keys) => {
          const id = keys[0]?.toString();
          if (id) void helpers.setValue(id);
        }}
        trigger={(props) => (
          <div ref={triggerRef} className={styles.triggerContainer}>
            <InputButton
              data-testid={`${props.testID}-button`}
              rightIcon="expand_more"
              isDisabled={props.isDisabled || isForbidden}
            >
              <Text
                style="input/text-field"
                color={primaryReviewerId ? "text-base" : "text-placeholder"}
              >
                {triggerLabel}
              </Text>
            </InputButton>
          </div>
        )}
      />
      <Text style="global/body-secondary" color="text-weak">
        {t(
          "Select a reviewer to assess completed checks and decide the final outcome"
        )}
      </Text>
      {showStaleReviewerWarning ? (
        <div data-testid="stale-reviewer-warning">
          <Text style="global/body-secondary" color="text-danger">
            {t(
              "The previously assigned reviewer is no longer eligible. Please select a new reviewer."
            )}
          </Text>
        </div>
      ) : null}
      {showError ? (
        <div data-testid="error-message">
          <Text style="global/body-secondary" color="text-danger">
            {meta.error}
          </Text>
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  labelRow: css({
    display: "flex",
    gap: "var(--space-1)",
  }),
  triggerContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1-5)",
  }),
};
